import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TimeEntryDetailsModal from './TimeEntryDetailsModal';

const TimeEntryTable = ({ 
  entries = [], 
  onEditEntry, 
  onDeleteEntry,
  onBulkAction,
  loading = false,
  isColumnsHidden = false
}) => {
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc'
  });
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    entry: null
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEntries(new Set(entries.map(entry => entry.id)));
    } else {
      setSelectedEntries(new Set());
    }
  };

  const handleSelectEntry = (entryId, checked) => {
    const newSelected = new Set(selectedEntries);
    if (checked) {
      newSelected?.add(entryId);
    } else {
      newSelected?.delete(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleSort = (key) => {
    const direction = sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const handleRowClick = (entry) => {
    setDetailsModal({
      isOpen: true,
      entry
    });
  };


  const getEntryTypeIcon = (type) => {
    const typeConfig = {
      kiosk: { icon: 'Monitor', color: 'text-primary' },
      manual: { icon: 'Edit', color: 'text-accent' },
      qr: { icon: 'QrCode', color: 'text-success' },
      mobile: { icon: 'Smartphone', color: 'text-secondary' }
    };

    const config = typeConfig?.[type] || typeConfig?.kiosk;
    return <Icon name={config?.icon} size={16} className={config?.color} />;
  };

  const getEntryTypeLabel = (type) => {
    const typeLabels = {
      kiosk: 'Kiosk',
      manual: 'Manuell',
      qr: 'QR-Code',
      mobile: 'Mobil'
    };
    return typeLabels?.[type] || type;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    const [h = '00', m = '00'] = timeString.split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  // Format time with minimum of 8:00 for "Kommen" column
  const formatTimeWithMin = (timeString, minTime = '08:00') => {
    if (!timeString) return '--:--';
    
    const [h = '00', m = '00'] = timeString.split(':');
    const [minH = '08', minM = '00'] = minTime.split(':');
    
    const currentMinutes = parseInt(h) * 60 + parseInt(m);
    const minMinutes = parseInt(minH) * 60 + parseInt(minM);
    
    if (currentMinutes < minMinutes) {
      return `${minH.padStart(2, '0')}:${minM.padStart(2, '0')}`;
    }
    
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  // Format time with maximum of 17:00 for "Gehen" column
  const formatTimeWithMax = (timeString, maxTime = '17:00') => {
    if (!timeString) return '--:--';
    
    const [h = '00', m = '00'] = timeString.split(':');
    const [maxH = '17', maxM = '00'] = maxTime.split(':');
    
    const currentMinutes = parseInt(h) * 60 + parseInt(m);
    const maxMinutes = parseInt(maxH) * 60 + parseInt(maxM);
    
    if (currentMinutes > maxMinutes) {
      return `${maxH.padStart(2, '0')}:${maxM.padStart(2, '0')}`;
    }
    
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Expecting YYYY-MM-DD, format as DD.MM.YYYY
    const [y, m, d] = dateString.split('-');
    if (!y || !m || !d) return dateString;
    return `${d}.${m}.${y}`;
  };

  const calculateDuration = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return '--';
    
    const start = new Date(`2000-01-01T${clockIn}`);
    const end = new Date(`2000-01-01T${clockOut}`);
    const diff = end - start;
    
    if (diff < 0) return '--';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  const formatDurationFromHours = (hours) => {
    if (!hours || hours === 0) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Format duration with maximum of 8 hours for Vollzeit and Teilzeit employees
  const formatDurationWithMax = (hours, hireType) => {
    if (!hours || hours === 0) return '0h 0m';
    
    // Apply 8-hour maximum for Vollzeit and Teilzeit employees
    const maxHours = (hireType === 'Vollzeit' || hireType === 'Teilzeit') ? 8 : hours;
    const cappedHours = Math.min(hours, maxHours);
    
    const h = Math.floor(cappedHours);
    const m = Math.round((cappedHours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Calculate Dauer S for Teilzeit employees like Vollzeit (hours over 8)
  const calculateDauerSForTeilzeit = (entry) => {
    const hireType = entry?.employee?.hire_type || entry?.employee?.hireType;
    const dauerT = entry?.dauerT || 0;
    
    if (hireType === 'Teilzeit' && dauerT > 8) {
      return Math.max(0, dauerT - 8);
    }
    
    return entry?.dauerS || 0;
  };

  const calculateMiniJobDuration = (clockIn, clockOut, type) => {
    if (!clockIn || !clockOut) return '0h 0m';
    
    const start = new Date(`2000-01-01T${clockIn}`);
    const end = new Date(`2000-01-01T${clockOut}`);
    const diff = end - start;
    
    if (diff < 0) return '0h 0m';
    
    const totalHours = diff / (1000 * 60 * 60);
    
    const formatHoursMinutes = (hours) => {
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      return `${h}h ${m}m`;
    };
    
    switch (type) {
      case 'first2': // Dauer - first 2 hours only
        return formatHoursMinutes(Math.min(2, totalHours));
      case 'remaining': // Dauer S - remaining hours after first 2
        return formatHoursMinutes(Math.max(0, totalHours - 2));
      case 'total': // Dauer T - total hours
        return formatHoursMinutes(totalHours);
      default:
        return '0h 0m';
    }
  };

  // Helper function to calculate dynamic pause based on hours worked
  const calculatePauseHours = (totalHours) => {
    if (totalHours <= 0) return 0;
    // 15 minutes (0.25 hours) pause for every 2 hours worked
    const pauseBlocks = Math.floor(totalHours / 2);
    const calculatedPause = pauseBlocks * 0.25; // 15 minutes = 0.25 hours
    // Cap maximum pause at 1 hour
    return Math.min(calculatedPause, 1);
  };

  // Helper function to calculate Gehalt S for time entries
  const calculateGehaltS = (entry) => {
    if (!entry?.employee) return 0;
    
    // Use stored values: Dauer, Dauer S, Dauer T
    const dauer = entry?.dauer || 0;
    const dauerS = entry?.dauerS || 0;
    const dauerT = entry?.dauerT || 0;
    const tagesrate = entry?.employee?.tagesrate || 0;
    
    // Calculate dynamic pause based on hours worked (max 1 hour)
    const pauseHours = calculatePauseHours(dauerT);
    
    // Calculate Gehalt S = (Dauer T - dynamic pause) / 8 × Tagesrate
    const effectiveHours = Math.max(0, dauerT - pauseHours);
    return (effectiveHours / 8) * tagesrate;
  };

  const sortedEntries = [...entries]?.sort((a, b) => {
    const { key, direction } = sortConfig;
    let aValue = a?.[key];
    let bValue = b?.[key];

    if (key === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="flex items-center justify-center space-x-3">
          <Icon name="Loader2" size={20} className="text-primary animate-spin" />
          <span className="text-text-secondary">Zeiteinträge werden geladen...</span>
        </div>
      </div>
    );
  }

  if (entries?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Clock" size={48} className="text-text-secondary mx-auto mb-4" />
        <h3 className="text-lg font-medium text-text-primary mb-2">Keine Zeiteinträge gefunden</h3>
        <p className="text-text-secondary">
          Passen Sie Ihre Filter oder den Datumsbereich an, um mehr Einträge zu sehen.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Bulk Actions Bar */}
      {selectedEntries?.size > 0 && (
        <div className="bg-primary/5 border-b border-border p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-primary font-medium">
              {selectedEntries?.size} Einträge ausgewählt
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('approve', Array.from(selectedEntries))}
                iconName="Check"
                iconPosition="left"
              >
                Auswahl genehmigen
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('flag', Array.from(selectedEntries))}
                iconName="Flag"
                iconPosition="left"
              >
                Zur Überprüfung markieren
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onBulkAction('delete', Array.from(selectedEntries));
                  setSelectedEntries(new Set());
                }}
                iconName="Trash2"
                iconPosition="left"
                className="text-error border-error hover:bg-error hover:text-white"
              >
                Ausgewählte löschen
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedEntries(new Set())}
                iconName="X"
              >
                Auswahl aufheben
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left p-4 w-12">
                <input
                  type="checkbox"
                  checked={selectedEntries?.size === entries?.length && entries?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border"
                />
              </th>
              <th 
                className="text-left p-4 cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort('employee')}
              >
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-medium text-text-primary">Mitarbeiter</span>
                  <Icon 
                    name={sortConfig?.key === 'employee' ? 
                      (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 
                      'ChevronsUpDown'
                    } 
                    size={14} 
                    className="text-text-secondary" 
                  />
                </div>
              </th>
              <th 
                className="text-left p-4 cursor-pointer hover:bg-muted/80 transition-colors"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-medium text-text-primary">Datum</span>
                  <Icon 
                    name={sortConfig?.key === 'date' ? 
                      (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 
                      'ChevronsUpDown'
                    } 
                    size={14} 
                    className="text-text-secondary" 
                  />
                </div>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-text-primary">Kommen</span>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-text-primary">Gehen</span>
              </th>
              {!isColumnsHidden && (
                <th className="text-left p-4">
                  <span className="text-sm font-medium text-text-primary">Gehen S</span>
                </th>
              )}
              <th className="text-left p-4">
                <span className="text-sm font-medium text-text-primary">Dauer</span>
              </th>
              {!isColumnsHidden && (
                <th className="text-left p-4">
                  <span className="text-sm font-medium text-text-primary">Dauer S</span>
                </th>
              )}
              {!isColumnsHidden && (
                <th className="text-left p-4">
                  <span className="text-sm font-medium text-text-primary">Dauer T</span>
                </th>
              )}
              {!isColumnsHidden && (
                <th className="text-left p-4">
                  <span className="text-sm font-medium text-text-primary">Gehalt S</span>
                </th>
              )}
              
              <th className="text-left p-4" data-column="Aktionen">
                <span className="text-sm font-medium text-text-primary">Aktionen</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries?.map((entry) => (
              <tr 
                key={entry?.id} 
                className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => handleRowClick(entry)}
              >
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedEntries?.has(entry?.id)}
                    onChange={(e) => handleSelectEntry(entry?.id, e?.target?.checked)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-primary">
                        {entry?.employee?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-text-primary">{entry?.employee?.name}</div>
                      <div className="text-xs text-text-secondary">{entry?.employee?.employee_code || entry?.employee?.id}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-text-primary">{formatDate(entry?.date)}</div>
                  <div className="text-xs text-text-secondary">{entry?.employee?.hire_type || entry?.employee?.hireType}</div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-text-primary">{formatTimeWithMin(entry?.clockIn)}</div>
                  {entry?.originalClockIn && entry?.originalClockIn !== entry?.clockIn}
                </td>
                <td className="p-4">
                  <div className="text-sm text-text-primary">{formatTimeWithMax(entry?.clockOut)}</div>
                  {entry?.originalClockOut && entry?.originalClockOut !== entry?.clockOut}
                </td>
                {!isColumnsHidden && (
                  <td className="p-4">
                    <div className="text-sm text-text-primary font-medium">{formatTime(entry?.clockOutReal || entry?.clockOut)}</div>
                    <div className="text-xs text-text-secondary mt-1">
                      Ausstempeln
                    </div>
                  </td>
                )}
                <td className="p-4">
                  <span className="text-sm text-text-primary">
                    {formatDurationWithMax(entry?.dauer || 0, entry?.employee?.hire_type || entry?.employee?.hireType)}
                  </span>
                </td>
                {!isColumnsHidden && (
                  <td className="p-4">
                    <span className="text-sm text-text-primary font-medium">
                      {formatDurationFromHours(calculateDauerSForTeilzeit(entry))}
                    </span>
                  </td>
                )}
                {!isColumnsHidden && (
                  <td className="p-4">
                    <span className="text-sm text-text-primary font-medium">
                      {formatDurationFromHours(entry?.dauerT || 0)}
                    </span>
                  </td>
                )}
                {!isColumnsHidden && (
                  <td className="p-4">
                    <span className="text-sm text-text-primary font-medium">
                      {(() => {
                        const gehaltSValue = calculateGehaltS(entry);
                        return gehaltSValue > 0 ? `€${gehaltSValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '€0,00';
                      })()}
                      {((entry?.employee?.hire_type || entry?.employee?.hireType) === 'Mini-job' || (entry?.employee?.hire_type || entry?.employee?.hireType) === 'Vollzeit') && entry?.dauerT > 0 && (
                        <div className="text-xs text-text-secondary mt-1">
                          {(() => {
                            const pauseHours = calculatePauseHours(entry.dauerT || 0);
                            const pauseMinutes = Math.round(pauseHours * 60);
                            return `Dauer: ${formatDurationFromHours(entry.dauer || 0)}, Dauer S: ${formatDurationFromHours(entry.dauerS || 0)}, Gesamt: (${formatDurationFromHours(entry.dauerT || 0)} - ${pauseMinutes}min Pause) ÷ 8h × €${entry.employee?.tagesrate || 0} = €${calculateGehaltS(entry).toFixed(2)}`;
                          })()}
                        </div>
                      )}
                    </span>
                  </td>
                )}
                
                <td className="p-4" data-column="Aktionen" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditEntry(entry)}
                      iconName="Edit"
                      className="text-text-secondary hover:text-primary"
                      title="Zeiteintrag bearbeiten"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteEntry(entry)}
                      iconName="Trash2"
                      className="text-text-secondary hover:text-error"
                      title="Zeiteintrag löschen"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Time Entry Details Modal */}
      <TimeEntryDetailsModal
        entry={detailsModal?.entry}
        isOpen={detailsModal?.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, entry: null })}
      />
    </div>
  );
};

export default TimeEntryTable;